import { Box, Card, Grid, TextField, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import "ag-grid-community/dist/styles/ag-theme-material.css";

import {
  CellValueChangedEvent,
  ColumnApi,
  CsvExportParams,
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
  RowDataChangedEvent,
  RowNode
} from "ag-grid-community";
import {
  AgGridColumn,
  AgGridColumnGroupProps,
  AgGridColumnProps,
  AgGridReact
} from "ag-grid-react";
import { Icon } from "@iconify/react";
import downloadFill from "@iconify/icons-eva/download-fill";
import deleteFill from "@iconify/icons-eva/person-delete-fill";
import addIcon from "@iconify/icons-eva/person-add-outline";

import { customFrameworkComponents } from "utils/Haim/agGridCellRenderers";
import useSettings from "hooks/useSettings";
import { useCallback, useEffect, useState } from "react";
import localeHe from "locales/aggrid.he";
import useLocales from "hooks/useLocales";
import { LoadingButton } from "@material-ui/lab";
import { AgGridReactProps, AgReactUiProps } from "ag-grid-react/lib/interfaces";
import { getObjectPropertyByString, sleep } from "utils/Haim/utils";
import { GenericMenuRowProps } from "./GenericMenu";
import { useTableCheckbox } from "hooks/SharedGrid/useTableCheckbox";
import { GridWithPaginationCheckbox } from "./GridWithPaginationCheckbox";
import { RowsPerPage } from "./RowsPerPage";

type SharedGridProps = {
  tableColumns: AgGridColumnProps[];
  tableRows: any[];
  nodeIdColumnName?: string;
  isLoading?: boolean;
  showCheckboxes?: boolean;
  showSearchBar?: boolean;
  showNewButton?: boolean;
  showRowMoreMenu?: boolean;
  showDownloadAsCsvButton?: boolean;
  showDeleteButton?: boolean;
  additionalTopButtons?: JSX.Element[];
  onNewClicked?: () => Promise<void>;
  onDeleteClicked?: (rowNodes: RowNode[]) => Promise<void>;
  isGridEditable?: boolean;
  gridHeight?: string;
  customAgGridProps?: AgGridReactProps | AgReactUiProps | {};
  customAgGridColumnsProps?: AgGridColumnProps | AgGridColumnGroupProps | {};
  rowMoreMenuProps?: GenericMenuRowProps[] | null;
  showPagination?: boolean | undefined;
  paginationPageSize?: number | undefined;
  pageSizes?: number[] | undefined;
  customOverlayNoRowsTemplate?: (searchText: string) => string;
};

export default function SharedGrid({
  tableColumns,
  tableRows,
  nodeIdColumnName = null,
  isLoading = false,
  showCheckboxes = false,
  showRowMoreMenu = false,
  showSearchBar = false,
  showNewButton = false,
  showDownloadAsCsvButton = false,
  showDeleteButton = false,
  additionalTopButtons = [],
  onNewClicked = null,
  onDeleteClicked = null,
  isGridEditable = false,
  gridHeight = "60vh",
  customAgGridProps = {},
  customAgGridColumnsProps = {},
  rowMoreMenuProps = null,
  showPagination = false,
  paginationPageSize = 100,
  pageSizes = [100, 150, 200],
  customOverlayNoRowsTemplate
}: SharedGridProps) {
  const internalCustomAgGridProps: AgGridReactProps = {};

  if (nodeIdColumnName) {
    internalCustomAgGridProps.getRowNodeId = (data) =>
      getObjectPropertyByString(data, nodeIdColumnName);
    internalCustomAgGridProps.immutableData = true;
  }

  showCheckboxes = showCheckboxes || showDownloadAsCsvButton || showDeleteButton;

  const { themeDirection } = useSettings();
  const { translate } = useLocales();

  const theme = useTheme();

  const isLight = theme.palette.mode === "light";
  const alignValue = themeDirection === "ltr" ? "left" : "right";

  const [gridApi, setGridApi] = useState<GridApi>(null);
  const [columnApi, setColumnApi] = useState<ColumnApi>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [selectedCount, setSelectedCount] = useState<number>(0);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [gridColumns, setGridColumns] = useState<AgGridColumnProps[]>(tableColumns);
  const { checkboxState, updateCheckboxState, onCheckboxClick } = useTableCheckbox(
    showPagination,
    gridApi
  );

  useEffect(() => {
    let columns: AgGridColumnProps[] = [];
    if (showCheckboxes || showRowMoreMenu) {
      let sideColumn: AgGridColumnProps = {
        field: "sideColumnField",
        headerName: "",
        pinned: alignValue,
        width: showCheckboxes && showRowMoreMenu ? 70 : 50,
        resizable: false,
        suppressAutoSize: false,
        suppressSizeToFit: false
      };
      if (showRowMoreMenu) {
        sideColumn = {
          ...sideColumn,
          cellRenderer: "MenuRenderer",
          cellRendererParams: {
            menuRows: rowMoreMenuProps
          }
        };
      }
      if (showCheckboxes) {
        sideColumn = {
          ...sideColumn,
          checkboxSelection: true,
          headerCheckboxSelection: !showPagination,
          headerCheckboxSelectionFilteredOnly: !showPagination
        };
      }
      columns.push(sideColumn);
    }
    if (tableColumns) {
      columns = columns.concat(tableColumns);
    }
    setGridColumns(columns);
  }, [tableColumns, alignValue, rowMoreMenuProps, showCheckboxes, showPagination, showRowMoreMenu]);

  const defaultColDef = {
    sortable: true,
    resizable: true,
    // wrapText & autoHeight caused bad grid on mobile
    // wrapText: true, // wrap text is not good with custom renderer?
    autoHeight: true
    // filter: 'agTextColumnFilter'
  };

  const handleOnCellValueChanged = (event: CellValueChangedEvent) => {
    if (!isGridEditable) {
      return;
    }
    const field = event.column.getColId();
    const value = event.newValue;
    const updatedObject = {
      [field]: value
    };
    console.log(`User set new value for field: ${field} -> ${value} `, updatedObject);
    let rowToFlash = gridApi.getDisplayedRowAtIndex(event.rowIndex);
    let columnToFlash = event.column.getColId();
    gridApi.flashCells({ rowNodes: [rowToFlash], columns: [columnToFlash], flashDelay: 500 });
  };

  const autoSizeColumns = async () => {
    await sleep(500);
    if (!columnApi) {
      return;
    }

    // columnApi.autoSizeAllColumns();
    await sleep(100);
    (columnApi.getAllColumns() ?? []).forEach((col) => {
      // Enforcing a "soft" max width
      if (col.getActualWidth() > 500) {
        columnApi.setColumnWidth(col, 500);
      }
    });
  };
  const handleOnGridReady = (event: GridReadyEvent) => {
    setGridApi(event.api);
    setColumnApi(event.columnApi);
    autoSizeColumns();
  };

  const handleOnRowDataChanged = (event: RowDataChangedEvent) => {
    if (!columnApi) {
      return;
    }

    autoSizeColumns();
  };

  const handleOnSelectionChanged = (params: any) => {
    setSelectedCount(gridApi.getSelectedNodes().length);
  };

  const handleOnFirstDataRendered = (event: FirstDataRenderedEvent) => {
    autoSizeColumns();
  };

  useEffect(() => {
    if (!gridApi) {
      return;
    }

    gridApi.setQuickFilter(searchText.replaceAll("-", ""));
  }, [searchText, gridApi]);

  useEffect(() => {
    if (!gridApi) {
      return;
    }

    const rowsCount = tableRows?.length ?? 0;

    if (isLoading) {
      gridApi.showLoadingOverlay();
    } else if (!isLoading && rowsCount === 0) {
      gridApi.showNoRowsOverlay();
    } else {
      gridApi.hideOverlay();
    }
  }, [isLoading, gridApi, tableRows?.length]);

  const handleDownloadDataAsCsvClick = useCallback(() => {
    const params: CsvExportParams = {
      fileName: "webot-export.csv",
      onlySelected: true,
      onlySelectedAllPages: true
    };
    gridApi.exportDataAsCsv(params);
  }, [gridApi]);

  const handleNewClick = () => {
    if (!onNewClicked) {
      console.error("New+ was clicked, but no function was defined (onDeleteClicked)");
      return;
    }
    console.debug("On new+ clicked");
    onNewClicked();
  };

  const handleDeleteClick = async () => {
    if (!onDeleteClicked) {
      console.error("Delete was clicked, but no function was defined (onDeleteClicked)");
      return;
    }
    console.debug("delete clicked");
    setIsDeleting(true);
    await onDeleteClicked(gridApi.getSelectedNodes());
    setIsDeleting(false);
  };

  const totalRowsCount = (tableRows ?? []).length;
  const rowsCountText = showCheckboxes
    ? `${selectedCount}/${totalRowsCount} שורות נבחרו`
    : `${totalRowsCount} שורות`;

  return (
    <>
      <Box sx={{ mx: 1 }}>
        <Grid container direction="row" spacing={1} justifyContent="flex-start" alignItems="center">
          {showSearchBar && (
            <Grid item xs={12} md="auto">
              <TextField
                label={translate("general.search")}
                size="small"
                fullWidth
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Grid>
          )}
          {showNewButton && (
            <Grid item xs={12} md="auto">
              <LoadingButton
                color="info"
                loading={!gridApi || isDeleting}
                onClick={handleNewClick}
                variant="contained"
                loadingPosition="end"
                endIcon={<Icon icon={addIcon} />}
                fullWidth
              >
                {translate("general.new")}
              </LoadingButton>
            </Grid>
          )}
          {showDownloadAsCsvButton && (
            <Grid item xs={12} md="auto">
              <LoadingButton
                color="primary"
                loading={!gridApi}
                disabled={selectedCount === 0}
                onClick={handleDownloadDataAsCsvClick}
                variant="contained"
                loadingPosition="end"
                endIcon={<Icon icon={downloadFill} />}
                fullWidth
              >
                {translate("general.downloadDataAsCsv")}
              </LoadingButton>
            </Grid>
          )}
          {showDeleteButton && (
            <Grid item xs={12} md="auto">
              <LoadingButton
                color="error"
                loading={!gridApi || isDeleting}
                disabled={selectedCount === 0}
                onClick={handleDeleteClick}
                variant="contained"
                loadingPosition="end"
                endIcon={<Icon icon={deleteFill} />}
                fullWidth
              >
                {translate("general.delete")}
              </LoadingButton>
            </Grid>
          )}
          {additionalTopButtons.map((additionalButton, i) => (
            <Grid item xs={12} md="auto" key={i}>
              {additionalButton}
            </Grid>
          ))}
          <Grid item xs={12} sx={{ mt: 1, ml: 1 }}>
            <Box display="flex" alignItems="center">
              {showPagination && (
                <GridWithPaginationCheckbox
                  checkboxState={checkboxState}
                  onCheckboxClick={onCheckboxClick}
                />
              )}
              <Typography variant="body2" color={theme.palette.grey[isLight ? 500 : 200]}>
                {rowsCountText}
              </Typography>
              {showPagination && (
                <RowsPerPage
                  gridApi={gridApi}
                  initialPageSize={paginationPageSize}
                  pageSizes={pageSizes}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Card sx={{ mx: 1, mt: 1 }}>
        <Box sx={{ height: gridHeight }}>
          <div
            className={isLight ? "ag-theme-material" : "ag-theme-alpine-dark"}
            style={{ height: gridHeight, margin: -1 }}
          >
            <AgGridReact
              {...customAgGridProps}
              onGridReady={(event) => {
                handleOnGridReady(event);
                (customAgGridProps as AgGridReactProps).onGridReady?.(event);
              }}
              onFirstDataRendered={(event) => {
                handleOnFirstDataRendered(event);
                (customAgGridProps as AgGridReactProps).onFirstDataRendered?.(event);
              }}
              onRowDataChanged={(...args) => {
                handleOnRowDataChanged(...args);
                (customAgGridProps as AgGridReactProps).onRowDataChanged?.(...args);
              }}
              onRowDataUpdated={(event) => {
                handleOnRowDataChanged(event);
                (customAgGridProps as AgGridReactProps).onRowDataUpdated?.(event);
              }}
              onSelectionChanged={(params) => {
                handleOnSelectionChanged(params);
                (customAgGridProps as AgGridReactProps).onSelectionChanged?.(params);
              }}
              overlayNoRowsTemplate={
                customOverlayNoRowsTemplate ? customOverlayNoRowsTemplate(searchText) : undefined
              }
              onRowSelected={updateCheckboxState}
              onPaginationChanged={updateCheckboxState}
              pagination={showPagination}
              paginationPageSize={paginationPageSize}
              rowSelection="multiple"
              enableCellTextSelection
              stopEditingWhenCellsLoseFocus
              animateRows={false}
              suppressRowClickSelection
              suppressMovableColumns
              frameworkComponents={customFrameworkComponents}
              defaultColDef={defaultColDef}
              loadingOverlayComponent={"LoadingOverlay"}
              rowData={tableRows ?? []}
              onCellValueChanged={handleOnCellValueChanged}
              enableRtl={themeDirection !== "ltr"}
              suppressMenuHide
              localeTextFunc={(key, defaultValue) => {
                return localeHe[key] || defaultValue;
              }}
              tooltipShowDelay={0}
              {...internalCustomAgGridProps}
            >
              {(gridColumns ?? []).map((col, index) => (
                <AgGridColumn
                  {...col}
                  {...customAgGridColumnsProps}
                  key={col.field ?? index}
                  editable={!isGridEditable ? false : col.editable}
                />
              ))}
            </AgGridReact>
          </div>
        </Box>
      </Card>
    </>
  );
}
